import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import backendApi from "@/api/backendApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const accomplishmentTypes = [
  {
    id: "online_profiles",
    label: "Online profile",
    description: "Add links to online profiles (LinkedIn, GitHub, Behance)",
  },
  {
    id: "work_samples",
    label: "Work sample",
    description: "Showcase project work or sample links",
  },
  {
    id: "publications",
    label: "Publications",
    description: "White papers / journals / published articles",
  },
  {
    id: "presentations",
    label: "Presentation",
    description: "Slide decks / talks / online presentations",
  },
  {
    id: "patents",
    label: "Patent",
    description: "Details of patents you have filed",
  },
  {
    id: "certifications",
    label: "Certification",
    description: "Professional certifications you have completed",
  },
];

export default function AccomplishmentsSection({ profile }) {
  const queryClient = useQueryClient();
  const [activeDialog, setActiveDialog] = useState(null);

  // Matches entity structure
  const accomplishments = profile.accomplishments || {};

  const [formData, setFormData] = useState({
    title: "",
    url: "",
    platform: "",
    authority: "",
    year: "",
  });

  // MUTATIONS
  const addMutation = useMutation({
    mutationFn: (data) => backendApi.entities.UserProfile.addAccomplishment(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
      setActiveDialog(null);
      setFormData({ title: "", url: "", platform: "", authority: "", year: "" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => backendApi.entities.UserProfile.deleteAccomplishment(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["userProfile"]);
    },
  });

  const handleSave = (type) => {
    let payload = { ...formData, type };

    // Alignment with schema if needed
    if (type === "certifications") {
      payload = {
        type,
        name: formData.title,
        authority: formData.authority,
        year: formData.year,
      };
    }

    addMutation.mutate(payload);
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this accomplishment?")) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Accomplishments
      </h3>
      <p className="text-gray-500 mb-4">
        Showcase your certifications, work samples, online profiles, and more.
      </p>

      <div className="space-y-3">
        {accomplishmentTypes.map((type) => {
          const items = accomplishments[type.id] || [];

          return (
            <div key={type.id} className="border border-gray-100 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-gray-800">{type.label}</h4>
                  <p className="text-sm text-gray-500">{type.description}</p>

                  {items.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {items.map((item) => (
                        <div key={item.id} className="flex items-center justify-between group">
                          <p className="text-sm text-blue-600">
                            {item.title ||
                              item.platform ||
                              item.name ||
                              "Untitled"}
                          </p>
                          <button 
                            onClick={() => handleDelete(item.id)}
                            className="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setActiveDialog(type.id)}
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  {items.length > 0 ? "Edit" : "Add"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={!!activeDialog} onOpenChange={() => setActiveDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Add{" "}
              {accomplishmentTypes.find((t) => t.id === activeDialog)?.label}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {activeDialog === "certifications" ? (
              <>
                <div>
                  <Label>Certification Name</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="e.g. AWS Solutions Architect"
                  />
                </div>

                <div>
                  <Label>Issuing Authority</Label>
                  <Input
                    value={formData.authority}
                    onChange={(e) =>
                      setFormData({ ...formData, authority: e.target.value })
                    }
                    placeholder="e.g. Amazon Web Services"
                  />
                </div>

                <div>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) =>
                      setFormData({ ...formData, year: e.target.value })
                    }
                    placeholder="2024"
                  />
                </div>
              </>
            ) : activeDialog === "online_profiles" ? (
              <>
                <div>
                  <Label>Platform</Label>
                  <Input
                    value={formData.platform}
                    onChange={(e) =>
                      setFormData({ ...formData, platform: e.target.value })
                    }
                    placeholder="LinkedIn, GitHub"
                  />
                </div>

                <div>
                  <Label>Profile URL</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) =>
                      setFormData({ ...formData, url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    placeholder="Enter title"
                  />
                </div>

                <div>
                  <Label>URL</Label>
                  <Input
                    value={formData.url}
                    onChange={(e) =>
                      setFormData({ ...formData, url: e.target.value })
                    }
                    placeholder="https://..."
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActiveDialog(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleSave(activeDialog)}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
